import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import type { LabelType } from '../utils/labelPlaceholders.ts';

type PreviewError = { title: string; detail?: string };

const errorFor = (status: number, text: string): PreviewError => {
  if (status === 422) return { title: 'Etikett kann mit diesen Werten nicht gerendert werden.', detail: text };
  if (status === 503) return { title: 'Der Etiketten-Renderer ist gerade nicht erreichbar.' };
  if (status === 400) return { title: 'Etikettengröße (10–100 mm) oder Vorlage ungültig.' };
  return { title: 'Die Vorschau konnte nicht geladen werden.' };
};

/** Renders a template with sample values through our own API; labelize runs beside it (LC-1029). */
export const useLabelPreview = () => {
  const auth = useAuth();
  const [state, setState] = useState<{ objectUrl: string; error: PreviewError | null; loading: boolean }>({
    objectUrl: '',
    error: null,
    loading: false,
  });
  const objectUrlRef = useRef('');
  const abortRef = useRef<AbortController | null>(null);

  const render = useCallback(
    async (request: { zpl: string; width: number; height: number; labelType: LabelType; values: Record<string, string> }) => {
      // a slower, older render must never replace a newer image
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setState(s => ({ ...s, loading: true }));

      try {
        const response = await fetch(`${window._env_.API_URL}/rest/label-preview`, {
          method: 'POST',
          body: JSON.stringify(request),
          signal: controller.signal,
          headers: {
            authorization: `Bearer ${auth.user?.access_token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          const error = errorFor(response.status, await response.text());
          if (abortRef.current === controller) setState(s => ({ ...s, error, loading: false }));
          return;
        }

        const blob = await response.blob();
        if (abortRef.current !== controller) return;
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = URL.createObjectURL(blob);
        setState({ objectUrl: objectUrlRef.current, error: null, loading: false });
      } catch {
        if (abortRef.current === controller) setState(s => ({ ...s, error: errorFor(0, ''), loading: false }));
      }
    },
    [auth]
  );

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    []
  );

  return { ...state, render };
};
